import React, { useMemo, useState } from "react";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const COLORS = ["#2563EB", "#F97316", "#22C55E", "#8B5CF6", "#EF4444", "#0EA5E9", "#F59E0B", "#14B8A6"];
const SVG_HEIGHT = 320;
const MARGIN = { top: 28, right: 28, bottom: 52, left: 72 };
const POINT_RADIUS = 4;

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const FinancialsActualsLineChart = ({ data }) => {
  const [tooltip, setTooltip] = useState({
    show: false,
    x: 0,
    y: 0,
    label: "",
    value: 0,
  });

  const series = useMemo(() => {
    if (!Array.isArray(data) || !data.length) {
      return {
        years: [],
        maxValue: 0,
        rows: [],
      };
    }

    const yearMap = new Map();

    data.forEach((item) => {
      const monthIdx = Number(item?.month) - 1;
      const year = Number(item?.year);
      if (monthIdx < 0 || monthIdx > 11 || Number.isNaN(year)) return;

      if (!yearMap.has(year)) {
        yearMap.set(year, Array.from({ length: 12 }, () => null));
      }

      yearMap.get(year)[monthIdx] = Number(item?.revenue || 0);
    });

    const years = Array.from(yearMap.keys()).sort((a, b) => a - b);
    const rows = years.map((year) => ({
      year,
      values: yearMap.get(year),
    }));

    const maxValue = rows.reduce(
      (max, row) => Math.max(max, ...row.values.map((value) => (value == null ? 0 : value))),
      0
    );

    return { years, maxValue, rows };
  }, [data]);

  if (!series.years.length || !series.maxValue) {
    return <div className="financials-page__chart-empty">No actual revenue history available yet.</div>;
  }

  const chartWidth = 920;
  const innerWidth = chartWidth - MARGIN.left - MARGIN.right;
  const innerHeight = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;
  const xStep = innerWidth / Math.max(1, MONTH_LABELS.length - 1);
  const latestYear = series.years[series.years.length - 1];
  const oldestYear = series.years[0];
  const yearRange = Math.max(1, latestYear - oldestYear);

  const getX = (monthIdx) => MARGIN.left + monthIdx * xStep;
  const getY = (value) => MARGIN.top + innerHeight - (value / series.maxValue) * innerHeight;
  const getYearOpacity = (year) => {
    if (year === latestYear) return 1;
    const normalizedAge = (latestYear - year) / yearRange;
    return Math.max(0.24, 1 - normalizedAge * 0.6);
  };

  const showTooltip = (x, y, label, value) => {
    setTooltip({ show: true, x, y, label, value });
  };

  return (
    <div className="financials-page__chart">
      <div className="financials-page__chart-legend">
        {series.years.map((year, idx) => (
          <span key={year} className="financials-page__chart-legend-item">
            <span
              className="financials-page__chart-legend-dot"
              style={{
                backgroundColor: COLORS[idx % COLORS.length],
                opacity: getYearOpacity(year),
              }}
            />
            <span style={{ opacity: getYearOpacity(year) }}>{year}</span>
          </span>
        ))}
      </div>

      <div className="financials-page__chart-wrap">
        <svg
          width={chartWidth}
          height={SVG_HEIGHT}
          role="img"
          aria-label="Actual revenue by month with separate lines for each year"
          onMouseLeave={() => setTooltip((prev) => ({ ...prev, show: false }))}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((t, idx) => {
            const value = Math.round(series.maxValue * t);
            const y = MARGIN.top + innerHeight * (1 - t);
            return (
              <g key={idx}>
                <line
                  x1={MARGIN.left}
                  x2={chartWidth - MARGIN.right}
                  y1={y}
                  y2={y}
                  stroke="rgba(148, 163, 184, 0.22)"
                />
                <text
                  x={MARGIN.left - 10}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill="#94A3B8"
                  fontSize="12"
                >
                  {currencyFormatter.format(value)}
                </text>
              </g>
            );
          })}

          {MONTH_LABELS.map((label, monthIdx) => (
            <text
              key={label}
              x={getX(monthIdx)}
              y={SVG_HEIGHT - MARGIN.bottom + 22}
              textAnchor="middle"
              fill="#475569"
              fontSize="12"
              fontWeight="600"
            >
              {label}
            </text>
          ))}

          {series.rows.map((row, rowIdx) => {
            const path = row.values
              .map((value, monthIdx) => {
                if (value == null) return null;
                const command = monthIdx === 0 || row.values[monthIdx - 1] == null ? "M" : "L";
                return `${command} ${getX(monthIdx)} ${getY(value)}`;
              })
              .filter(Boolean)
              .join(" ");

            return (
              <g key={row.year}>
                <path
                  d={path}
                  fill="none"
                  stroke={COLORS[rowIdx % COLORS.length]}
                  strokeOpacity={getYearOpacity(row.year)}
                  strokeWidth="3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {row.values.map((value, monthIdx) => {
                  if (value == null) return null;
                  const x = getX(monthIdx);
                  const y = getY(value);
                  return (
                    <circle
                      key={`${row.year}-${monthIdx}`}
                      cx={x}
                      cy={y}
                      r={POINT_RADIUS}
                      fill="#fff"
                      stroke={COLORS[rowIdx % COLORS.length]}
                      strokeOpacity={getYearOpacity(row.year)}
                      strokeWidth="2.5"
                      tabIndex={0}
                      aria-label={`${MONTH_LABELS[monthIdx]} ${row.year}: ${currencyFormatter.format(
                        value
                      )} revenue`}
                      onMouseEnter={() =>
                        showTooltip(x, y, `${MONTH_LABELS[monthIdx]} ${row.year}`, value)
                      }
                      onMouseMove={() =>
                        showTooltip(x, y, `${MONTH_LABELS[monthIdx]} ${row.year}`, value)
                      }
                      onFocus={() => showTooltip(x, y, `${MONTH_LABELS[monthIdx]} ${row.year}`, value)}
                      onBlur={() => setTooltip((prev) => ({ ...prev, show: false }))}
                    />
                  );
                })}
              </g>
            );
          })}
        </svg>

        {tooltip.show && (
          <div
            className="financials-page__chart-tooltip"
            style={{
              left: `${Math.min(Math.max(tooltip.x, 90), chartWidth - 90)}px`,
              top: `${Math.max(tooltip.y - 8, 18)}px`,
            }}
          >
            <div className="financials-page__chart-tooltip-title">{tooltip.label}</div>
            <div className="financials-page__chart-tooltip-value">
              {currencyFormatter.format(tooltip.value)} revenue
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialsActualsLineChart;
